import React, { useState, useMemo, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormInput } from '../../components/Basic/FormInput';
import { useNavigate } from 'react-router-dom';
import { notification, Spin } from 'antd';
import * as yup from 'yup';
import style from './ChangePassword.module.css';
import Button from '../../components/Basic/button';
import Header from '../../components/Header/header';
import { validateToken, redirectToLogin } from '../../utils/auth/tokenValidation';
import { usePasswordPolicy } from '../../utils/hooks/usePasswordPolicy';

interface ChangePasswordForm {
  oldPassword: string;
  newPassword: string;
}

const API_URL = process.env.REACT_APP_API_URL;

const ChangePassword: React.FC = () => {
  const [message, contextHolder] = notification.useNotification();
  const [spinning, setSpinning] = useState(false);
  const navigate = useNavigate();
  usePasswordPolicy(); // still dispatches fetch on non-change-password pages

  const [localPolicy, setLocalPolicy] = useState<any>(null);

  useEffect(() => {
    const token = localStorage.getItem('loginToken');
    if (!token) return;
    fetch(`${API_URL}/api/password-policy/get`, {
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((res) => { if (res?.id !== undefined) setLocalPolicy(res); })
      .catch(() => {});
  }, []);

  const validationSchema = useMemo(() => {
    let pwSchema: yup.StringSchema<string> = yup.string().required('Password is required');
    if (localPolicy) {
      if (localPolicy.length > 0) pwSchema = pwSchema.min(localPolicy.length, `Password must be at least ${localPolicy.length} characters`);
      if (localPolicy.capitalLetters > 0) pwSchema = pwSchema.matches(/[A-Z]/, `Must contain at least ${localPolicy.capitalLetters} uppercase letter(s)`);
      if (localPolicy.numbers > 0) pwSchema = pwSchema.matches(/[0-9]/, `Must contain at least ${localPolicy.numbers} number(s)`);
      if (localPolicy.specialCharacters > 0) pwSchema = pwSchema.matches(/[!@#$%^&*(),.?":{}|<>]/, `Must contain at least ${localPolicy.specialCharacters} special character(s)`);
    } else {
      pwSchema = pwSchema.min(8, 'Password must be at least 8 characters').matches(/[A-Z]/, 'Must contain at least one uppercase letter').matches(/[0-9]/, 'Must contain at least one number').matches(/[!@#$%^&*(),.?":{}|<>]/, 'Must contain at least one special character');
    }
    return yup.object().shape({ oldPassword: yup.string().required('Old password is required'), newPassword: pwSchema });
  }, [localPolicy]);

  const hints = localPolicy
    ? [
        ...(localPolicy.length > 0 ? [{ label: `At least ${localPolicy.length} characters` }] : []),
        ...(localPolicy.capitalLetters > 0 ? [{ label: `${localPolicy.capitalLetters} uppercase letter(s) (A-Z)` }] : []),
        ...(localPolicy.numbers > 0 ? [{ label: `${localPolicy.numbers} number(s) (0-9)` }] : []),
        ...(localPolicy.specialCharacters > 0 ? [{ label: `${localPolicy.specialCharacters} special character(s)` }] : []),
      ]
    : [{ label: 'At least 8 characters' }, { label: 'One uppercase letter (A-Z)' }, { label: 'One number (0-9)' }, { label: 'One special character' }];

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<ChangePasswordForm>({
    defaultValues: { oldPassword: '', newPassword: '' },
    mode: 'onChange',
    resolver: (values, context, options) => yupResolver(validationSchema)(values, context, options),
  });

  const onSubmit: SubmitHandler<ChangePasswordForm> = async (data) => {
    if (!validateToken()) { redirectToLogin(); return; }
    try {
      setSpinning(true);
      const token = localStorage.getItem('loginToken');
      const response = await fetch(`${API_URL}/auth/change/password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ oldPassword: data.oldPassword, newPassword: data.newPassword ?? '' }),
      });
      const res = await response.json();

      if (res.status === '200' || response.ok) {
        localStorage.removeItem('passwordChangeRequired');
        message.open({ message: res.message || 'Password changed successfully', type: 'success', duration: 3 });
        setTimeout(() => { setSpinning(false); navigate('/'); }, 2000);
      } else if (res.status === '1004') {
        localStorage.clear();
        message.open({ message: 'Password changed successfully. Please login again', type: 'success', duration: 3 });
        setTimeout(() => { setSpinning(false); navigate('/'); }, 3000);
      } else if (res.status === '1005') {
        message.open({ message: res.message || 'You need to change your password', type: 'warning', duration: 3 });
        setTimeout(() => setSpinning(false), 2000);
      } else {
        message.open({ message: res.message || res.exceptionMsg || 'Password change failed', type: 'error', duration: 3 });
        setSpinning(false);
      }
    } catch (error: any) {
      message.open({ message: error.message || 'An error occurred', type: 'error', duration: 3 });
      setSpinning(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Spin spinning={spinning} fullscreen />
      {/* <Header /> */}
      <div className={style.container}>
        <div className={style.formContainer}>
          <h4>Change Password</h4>
          {hints.length > 0 && (
            <div className={style.passwordPolicy}>
              <ul className={style.policyList}>
                {hints.map((h, i) => <li key={i}>{h.label}</li>)}
              </ul>
            </div>
          )}
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormInput
              placeholder='Enter Your Old Password'
              Label='Old Password'
              classInput={style.Input}
              className={style.inputContainer}
              labelClass={style.Label}
              classError={style.Error}
              name='oldPassword'
              type='password'
              errors={errors}
              control={control}
            />
            <FormInput
              placeholder='Enter Your New Password'
              Label='New Password'
              classInput={style.Input}
              className={style.inputContainer}
              labelClass={style.Label}
              classError={style.Error}
              name='newPassword'
              type='password'
              errors={errors}
              control={control}
            />
            <div className={style.btn}>
              <Button Text='Change Password' buttonClass={style.buttonSubmit} Disable={spinning} />
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ChangePassword;
